import { useState } from 'react';
import { subscribeToWebPush, isWebPushSupported, ensurePushPermission } from '@/lib/pushNotifications';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';

/** Sticky banner shown once to prompt the user to enable push notifications. */
export function PushPermissionBanner() {
  const user = useAuthStore((s) => s.user);
  const push = useNotificationStore((s) => s.push);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('push-banner-dismissed') === '1';
  });
  const [subscribing, setSubscribing] = useState(false);

  if (!user || dismissed || !isWebPushSupported()) return null;
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') return null;
  if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return null;

  const handleEnable = async () => {
    setSubscribing(true);
    const granted = await ensurePushPermission();
    if (!granted) {
      push({ title: 'Notifications blocked', body: 'Allow notifications in your browser settings.', tone: 'error' });
      setSubscribing(false);
      return;
    }
    const ok = await subscribeToWebPush(user.id);
    if (ok) {
      push({ title: 'Notifications enabled', body: "You'll be notified when your orders update.", tone: 'success' });
    }
    setDismissed(true);
    localStorage.setItem('push-banner-dismissed', '1');
    setSubscribing(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('push-banner-dismissed', '1');
  };

  return (
    <div className="border-b border-brand-200 bg-brand-50 px-4 py-2.5 dark:border-brand-800 dark:bg-brand-950/40">
      <div className="mx-auto flex max-w-listing items-center justify-between gap-4">
        <p className="text-sm text-brand-800 dark:text-brand-200">
          🔔 <strong>Enable notifications</strong> to get real-time order updates and deals.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={subscribing}
            onClick={() => void handleEnable()}
            className="rounded bg-brand-700 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {subscribing ? 'Enabling…' : 'Enable'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-xs text-brand-500 hover:text-brand-700 dark:text-brand-400"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
